import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

export interface OpsiSelect {
  value: string
  label: string
  nonaktif?: boolean
}

export function Label({
  children,
  htmlFor,
  wajib = false,
}: {
  children: ReactNode
  htmlFor?: string
  wajib?: boolean
}) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-netral-700">
      {children}
      {wajib ? <span className="ml-0.5 text-red-600">*</span> : null}
    </label>
  )
}

export function Bantuan({ children, nada = 'netral' }: { children: ReactNode; nada?: 'netral' | 'bahaya' }) {
  return (
    <p className={`mt-1 text-xs ${nada === 'bahaya' ? 'text-red-600' : 'text-netral-500'}`}>{children}</p>
  )
}

export interface PropsInput extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode
  bantuan?: ReactNode
  galat?: string | null
}

export function Input({ label, bantuan, galat, className = '', id, ...sisa }: PropsInput) {
  const idAkhir = id ?? (typeof label === 'string' ? `in-${slug(label)}` : undefined)
  return (
    <div className="w-full">
      {label ? (
        <Label htmlFor={idAkhir} wajib={sisa.required}>
          {label}
        </Label>
      ) : null}
      <input
        id={idAkhir}
        className={`mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-netral-900 shadow-sm transition-colors placeholder:text-netral-400 disabled:bg-netral-100 ${
          galat ? 'border-red-400' : 'border-netral-300 focus:border-yayasan-600'
        } ${className}`}
        {...sisa}
      />
      {galat ? <Bantuan nada="bahaya">{galat}</Bantuan> : bantuan ? <Bantuan>{bantuan}</Bantuan> : null}
    </div>
  )
}

export interface PropsTextarea extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode
  bantuan?: ReactNode
  galat?: string | null
}

export function Textarea({ label, bantuan, galat, className = '', id, ...sisa }: PropsTextarea) {
  const idAkhir = id ?? (typeof label === 'string' ? `ta-${slug(label)}` : undefined)
  return (
    <div className="w-full">
      {label ? (
        <Label htmlFor={idAkhir} wajib={sisa.required}>
          {label}
        </Label>
      ) : null}
      <textarea
        id={idAkhir}
        rows={sisa.rows ?? 3}
        className={`mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-netral-900 shadow-sm transition-colors placeholder:text-netral-400 ${
          galat ? 'border-red-400' : 'border-netral-300 focus:border-yayasan-600'
        } ${className}`}
        {...sisa}
      />
      {galat ? <Bantuan nada="bahaya">{galat}</Bantuan> : bantuan ? <Bantuan>{bantuan}</Bantuan> : null}
    </div>
  )
}

export interface PropsSelect extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode
  bantuan?: ReactNode
  galat?: string | null
  opsi?: OpsiSelect[]
  placeholder?: string
}

export function Select({
  label,
  bantuan,
  galat,
  opsi,
  placeholder,
  className = '',
  id,
  children,
  ...sisa
}: PropsSelect) {
  const idAkhir = id ?? (typeof label === 'string' ? `sel-${slug(label)}` : undefined)
  return (
    <div className="w-full">
      {label ? (
        <Label htmlFor={idAkhir} wajib={sisa.required}>
          {label}
        </Label>
      ) : null}
      <select
        id={idAkhir}
        className={`mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-netral-900 shadow-sm transition-colors ${
          galat ? 'border-red-400' : 'border-netral-300 focus:border-yayasan-600'
        } ${className}`}
        {...sisa}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {opsi
          ? opsi.map((o) => (
              <option key={o.value} value={o.value} disabled={o.nonaktif}>
                {o.label}
              </option>
            ))
          : children}
      </select>
      {galat ? <Bantuan nada="bahaya">{galat}</Bantuan> : bantuan ? <Bantuan>{bantuan}</Bantuan> : null}
    </div>
  )
}

export function Checkbox({
  label,
  ...sisa
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-netral-700">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-netral-300 text-yayasan-700 focus:ring-yayasan-600"
        {...sisa}
      />
      <span>{label}</span>
    </label>
  )
}

export function KolomForm({
  children,
  kolom = 2,
}: {
  children: ReactNode
  kolom?: 1 | 2 | 3
}) {
  const kelas =
    kolom === 1 ? '' : kolom === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'
  return <div className={`grid grid-cols-1 gap-3 ${kelas}`}>{children}</div>
}

function slug(teks: string): string {
  return teks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}
